/**
 * Accessibility utilities and components
 */

/**
 * Screen reader only text - visually hidden but accessible to assistive technology
 */
export function ScreenReaderOnly({ children, as: Component = 'span' }) {
  return (
    <Component
      className="sr-only"
      style={{
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: 0,
      }}
    >
      {children}
    </Component>
  );
}

/**
 * Skip to main content link - appears on focus for keyboard users
 */
export function SkipLink({ href = '#main-content', children = 'Skip to main content' }) {
  return (
    <a
      href={href}
      className="skip-link sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:bg-accent focus:text-primary focus:px-4 focus:py-2 focus:rounded-md focus:font-medium focus:outline-none focus:ring-2 focus:ring-primary"
    >
      {children}
    </a>
  );
}

/**
 * Focus trap for modals and dialogs
 */
export function useFocusTrap(ref, isActive = true) {
  const handleKeyDown = (e) => {
    if (!isActive || !ref.current || e.key !== 'Tab') return;
    
    const focusableElements = ref.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    if (e.shiftKey && document.activeElement === firstElement) {
      e.preventDefault();
      lastElement?.focus();
    } else if (!e.shiftKey && document.activeElement === lastElement) {
      e.preventDefault();
      firstElement?.focus();
    }
  };
  
  return { onKeyDown: handleKeyDown };
}

/**
 * Announce message to screen readers
 */
export function useAnnounce() {
  const announce = (message, priority = 'polite') => {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', priority);
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  };
  
  return announce;
}

/**
 * Hook for managing focus when navigating between pages
 */
export function usePageFocus() {
  const focusMainContent = () => {
    const main = document.getElementById('main-content');
    if (main) {
      main.setAttribute('tabindex', '-1');
      main.focus();
      main.removeAttribute('tabindex');
    }
  };
  
  return { focusMainContent };
}

/**
 * Accessible icon button wrapper
 */
export function IconButton({ 
  icon: Icon, 
  label, 
  onClick, 
  className = '',
  size = 'md',
  ...props 
}) {
  const sizes = {
    sm: 'p-1.5',
    md: 'p-2',
    lg: 'p-3',
  };
  
  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };
  
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center justify-center rounded-lg text-gray-600 hover:text-primary hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 ${sizes[size]} ${className}`}
      aria-label={label}
      {...props}
    >
      <Icon className={iconSizes[size]} aria-hidden="true" />
      <ScreenReaderOnly>{label}</ScreenReaderOnly>
    </button>
  );
}

/**
 * Loading state with proper ARIA attributes
 */
export function LoadingState({ isLoading, loadingText = 'Loading...', children }) {
  return (
    <div
      aria-busy={isLoading}
      aria-live="polite"
    >
      {isLoading ? (
        <div role="status">
          <ScreenReaderOnly>{loadingText}</ScreenReaderOnly>
          {children}
        </div>
      ) : (
        children
      )}
    </div>
  );
}

/**
 * Error message component with proper ARIA role
 */
export function ErrorMessage({ children, id }) {
  if (!children) return null;
  
  return (
    <div
      id={id}
      role="alert"
      aria-live="assertive"
      className="text-red-600 text-sm mt-1"
    >
      {children}
    </div>
  );
}

/**
 * Form field wrapper with accessibility features
 */
export function FormField({
  label,
  htmlFor,
  required = false,
  error,
  hint,
  children,
}) {
  const errorId = error ? `${htmlFor}-error` : undefined;
  const hintId = hint ? `${htmlFor}-hint` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;
  
  return (
    <div className="space-y-1">
      <label htmlFor={htmlFor} className="form-label">
        {label}
        {required && <span className="text-red-500 ml-1" aria-hidden="true">*</span>}
        {required && <ScreenReaderOnly>(required)</ScreenReaderOnly>}
      </label>
      
      {hint && (
        <p id={hintId} className="text-sm text-gray-500">
          {hint}
        </p>
      )}
      
      {/* Clone children to add aria-describedby */}
      {children}
      
      {error && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
    </div>
  );
}
